import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Store } from 'lucide-react';
import { SocialLink } from '../components/SocialLink';
import { useUserByUsername, useTrackEvent } from '../hooks/useQueries';

export function ProfilePage() {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const { data: user, isLoading: loading, error } = useUserByUsername(username || '');
  const trackEventMutation = useTrackEvent();

  // Track profile view when component mounts
  useEffect(() => {
    if (user && username) {
      trackEventMutation.mutate({
        userId: user.id,
        type: 'profile_view',
        metadata: { username }
      });
    }
  }, [user, username]);

  const handleSocialLinkClick = (linkId: string) => {
    if (user) {
      trackEventMutation.mutate({
        userId: user.id,
        type: 'social_link_click',
        targetId: linkId
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!user || error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="text-center bg-white rounded-lg shadow-sm p-8 max-w-md w-full">
          <div className="bg-gray-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Profile not found</h2>
          <p className="text-gray-600 mb-6">The user @{username} doesn't exist.</p>
          <button
            onClick={() => navigate('/')}
            className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium rounded-lg transition duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-8 px-4">
      <div className="max-w-md mx-auto">
        {/* Profile Card */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
          {/* Profile Header with Gradient */}
          <div className="h-32 bg-gradient-to-r from-blue-600 to-indigo-600"></div>
          
          {/* Profile Info */}
          <div className="px-6 pb-6 -mt-12">
            {/* Avatar */}
            <div className="flex justify-center">
              {user.avatar ? (
                <img
                  src={user.avatar}
                  alt={user.displayName}
                  className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                />
              ) : (
                <div className="bg-gray-200 border-4 border-white rounded-full w-24 h-24 flex items-center justify-center shadow-lg">
                  <span className="text-2xl font-bold text-blue-600">
                    {user.displayName.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
            </div>
            
            {/* Name and Username */}
            <div className="text-center mt-4">
              <h1 className="text-2xl font-bold text-gray-900">
                {user.displayName}
              </h1>
              <p className="text-blue-600 font-medium mt-1">
                @{user.username}
              </p>
              
              {/* Bio */}
              {user.bio && (
                <p className="text-gray-700 mt-4 leading-relaxed whitespace-pre-line text-center">
                  {user.bio}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Shop Button */}
        <button
          onClick={() => navigate(`/u/${username}/shop`)}
          className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-4 px-6 rounded-lg font-semibold shadow-sm transition duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 mb-6"
        >
          <div className="flex items-center justify-center gap-3">
            <Store className="w-5 h-5" />
            <span>Shop Collection</span>
          </div>
        </button>

        {/* Social Links */}
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-gray-900 text-center">Connect with me</h2>
          {user.socialLinks.filter(link => link.isActive).map(link => (
            <SocialLink 
              key={link.id} 
              platform={link.platform}
              url={link.url}
              title={link.title}
              onClick={() => handleSocialLinkClick(link.id)}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="text-center py-8">
          <p className="text-sm text-gray-500">
            Made with ❤️ for creators
          </p>
        </div>
      </div>
    </div>
  );
}